/**
 * Tag search + prefix listing for the gateway AI. Uses the gateway's
 * `/api/tags` endpoint (full tag list, lite) and ranks client-side so
 * we don't need a new backend search endpoint. Mirrors the editor's
 * ranking semantics (exact_name > exact_path > name_prefix >
 * segment_exact > segment_prefix > name_substring > path_substring)
 * so the same prompt instructions work on both surfaces.
 *
 * Cache is keyed at the module level (60s TTL). Invalidate after tag
 * mutations.
 */

import * as api from "../../api";

const CACHE_TTL_MS = 60_000;
let _cache = null;
let _cacheAt = 0;

export function invalidateTagCache() {
  _cache = null;
  _cacheAt = 0;
}

async function loadIndex({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cache && now - _cacheAt < CACHE_TTL_MS) return _cache;
  let tree;
  try {
    // The flat `/api/tags` endpoint only carries each tag's LOCAL path
    // segment, not its folder-qualified full path (see `list_tags` in
    // tools/readOnly.js) — a tag named "Battery_Voltage" living under
    // default/solis comes back with no way to tell it apart from a
    // root-level tag of the same name. Building the search index from
    // that endpoint silently broke every prefix/folder match for a
    // nested tag (control-seat-source#26 / BETA-48): `default/solis`
    // matched nothing because the index never had that folder-qualified
    // path in the first place, while a coincidentally-named flat
    // `Solis_*` tag matched fine on its bare name. The folder-tree
    // endpoint computes the real fullPath server-side, same source
    // `list_tags` already uses — use it here too so both tools agree.
    tree = await api.getFolderTree();
  } catch (err) {
    if (_cache) return _cache; // serve stale on transient failure
    throw err;
  }
  const browsers = Array.isArray(tree?.browsers) ? tree.browsers : [];
  const flat = [];
  for (const b of browsers) {
    for (const t of b?.tags || []) {
      if (!t) continue;
      const fullPath = t.fullPath || t.full_path || t.path || t.name || "";
      if (!fullPath) continue;
      const lastSlash = Math.max(fullPath.lastIndexOf("/"), fullPath.lastIndexOf("."));
      const folderPath = lastSlash >= 0 ? fullPath.slice(0, lastSlash) : "";
      const name = t.name || (lastSlash >= 0 ? fullPath.slice(lastSlash + 1) : fullPath);
      flat.push({
        id: t.tagId ?? t.id ?? null,
        fullPath,
        name,
        folderPath,
        type: t.type || t.value_type_name || null,
      });
    }
  }
  _cache = flat;
  _cacheAt = now;
  return flat;
}

function matchInfo(entry, qLower) {
  const path = entry.fullPath || "";
  const name = entry.name || "";
  const nameL = name.toLowerCase();
  const pathL = path.toLowerCase();
  if (nameL === qLower) return { rank: 0, matchType: "exact_name" };
  if (pathL === qLower) return { rank: 1, matchType: "exact_path" };
  if (nameL.startsWith(qLower)) return { rank: 2, matchType: "name_prefix" };
  // Path-segment matches — split on both delimiters so editor-style
  // dot paths AND gateway-style slash paths rank the same way.
  const segs = pathL.split(/[./]/);
  for (const s of segs) {
    if (s === qLower) return { rank: 3, matchType: "segment_exact" };
  }
  for (const s of segs) {
    if (s.startsWith(qLower)) return { rank: 4, matchType: "segment_prefix" };
  }
  if (nameL.includes(qLower)) return { rank: 5, matchType: "name_substring" };
  if (pathL.includes(qLower)) return { rank: 6, matchType: "path_substring" };
  return null;
}

export async function searchTags(query, { limit = 25, type = "" } = {}) {
  const q = typeof query === "string" ? query.trim() : "";
  if (!q) return [];
  const qLower = q.toLowerCase();
  const idx = await loadIndex();
  const out = [];
  for (const entry of idx) {
    if (type && entry.type && String(entry.type).toLowerCase() !== type.toLowerCase()) continue;
    const m = matchInfo(entry, qLower);
    if (m) out.push({ ...entry, ...m });
  }
  out.sort((a, b) => a.rank - b.rank || a.fullPath.localeCompare(b.fullPath));
  return out.slice(0, limit);
}

export async function listTagsByPrefix(prefix, { limit = 200, depth = Infinity, type = "" } = {}) {
  const p = typeof prefix === "string" ? prefix.trim() : "";
  if (!p) return [];
  // Trailing-slash tolerant: "default/solis" and "default/solis/" must
  // behave identically — a user (or the model) copying a folder path
  // from the UI often carries the trailing slash along.
  const pLower = p.replace(/\/+$/, "").toLowerCase();
  if (!pLower) return [];
  // Segment-boundary match, not a raw string prefix: "default/solis"
  // must match "default/solis/battery_voltage" (and the exact tag
  // "default/solis" itself, for a tag living directly at that path) but
  // NOT "default/solis-backup/foo" — a plain startsWith would treat the
  // folder "solis-backup" as being under "solis".
  const pWithSlash = `${pLower}/`;
  // Count the path segments in the prefix so we can apply a relative
  // depth filter — depth=1 means "direct children only".
  const prefixSegs = pLower.split(/[./]/).filter(Boolean).length;
  const idx = await loadIndex();
  const out = [];
  for (const entry of idx) {
    const path = (entry.fullPath || "").toLowerCase();
    if (path !== pLower && !path.startsWith(pWithSlash)) continue;
    if (type && entry.type && String(entry.type).toLowerCase() !== type.toLowerCase()) continue;
    if (Number.isFinite(depth)) {
      const entrySegs = path.split(/[./]/).filter(Boolean).length;
      if (entrySegs - prefixSegs > depth) continue;
    }
    out.push(entry);
    if (out.length >= limit) break;
  }
  return out;
}

export async function getTagByPath(tagPath) {
  if (!tagPath) return null;
  const idx = await loadIndex();
  return idx.find((t) => t.fullPath === tagPath) || null;
}
